// One card per journal entry type. `size="lg"` is the permalink view.
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Flex, Grid, Image, Link, Text } from "@chakra-ui/react";
import { ACCENT, BG, BODY, FG, GRID, INSET, MONO, MUTED, PANEL, SERIF, alpha } from "../theme";
import { Lightbox, VideoEmbed } from "../components/Media";
import { hostOf } from "./journal";
import VoiceNote from "../components/VoiceNote";

// open.spotify.com/track/ID → embeddable player
const spotifyEmbed = (url) => {
  const m = String(url || "").match(/open\.spotify\.com\/(?:intl-[a-z]+\/)?(track|album|playlist|episode|show|artist)\/([A-Za-z0-9]+)/);
  return m ? { src: `https://open.spotify.com/embed/${m[1]}/${m[2]}`, tall: m[1] !== "track" && m[1] !== "episode" } : null;
};

const TYPE_META = {
  thought: { label: "Thought", glyph: "✦" },
  voice: { label: "Voice note", glyph: "◉" },
  quote: { label: "Quote", glyph: "❝" },
  photo: { label: "Photo", glyph: "◐" },
  video: { label: "Video", glyph: "▶" },
  link: { label: "Link", glyph: "↗" },
};

const Meta = ({ e, link = true, color = MUTED }) => {
  const m = TYPE_META[e.audio && e.type === "thought" ? "voice" : e.type] || TYPE_META.thought;
  const inner = (
    <Flex
      align="center"
      gap={2}
      fontFamily={MONO}
      fontSize="10px"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color={color}
    >
      <Text as="span" color={e.type === "quote" ? ACCENT : color}>
        {m.glyph}
      </Text>
      <Text as="span">{m.label}</Text>
      <Text as="span" opacity={0.5}>
        /
      </Text>
      <Text as="span">{e.dateLabel}</Text>
      {link && (
        <Text as="span" ml="auto" opacity={0} transition="opacity .2s" className="meta-arrow">
          ↗
        </Text>
      )}
    </Flex>
  );
  return link ? (
    <Link as={RouterLink} to={`/journal/${e.id}`} _hover={{ textDecoration: "none", color: FG }} display="block">
      {inner}
    </Link>
  ) : (
    inner
  );
};

const Tags = ({ tags }) =>
  tags.length ? (
    <Flex gap={2} wrap="wrap" mt={4}>
      {tags.map((t) => (
        <Link
          as={RouterLink}
          key={t}
          to={`/journal?tag=${encodeURIComponent(t)}`}
          fontFamily={MONO}
          fontSize="10px"
          color={MUTED}
          letterSpacing="0.06em"
          _hover={{ color: ACCENT, textDecoration: "none" }}
        >
          #{t}
        </Link>
      ))}
    </Flex>
  ) : null;

const Shell = ({ children, pad = true, ...rest }) => (
  <Box
    bg={PANEL}
    border={`1px solid ${GRID}`}
    borderRadius="2xl"
    overflow="hidden"
    position="relative"
    transition="border-color .3s, transform .5s cubic-bezier(0.22, 1, 0.36, 1)"
    _hover={{ borderColor: "#3a3a41", "& .meta-arrow": { opacity: 1 } }}
    p={pad ? { base: 5, md: 6 } : 0}
    {...rest}
  >
    {children}
  </Box>
);

const ThoughtCard = ({ e, size, link }) => (
  <Shell>
    <Meta e={e} link={link} />
    {e.title && (
      <Text color={FG} fontWeight={600} fontSize="lg" mt={5} letterSpacing="-0.02em">
        {e.title}
      </Text>
    )}
    <Text
      color={FG}
      fontSize={size === "lg" ? { base: "26px", md: "36px" } : { base: "20px", md: "22px" }}
      fontWeight={500}
      lineHeight="1.35"
      letterSpacing="-0.025em"
      mt={e.title ? 2 : 5}
      whiteSpace="pre-line"
    >
      {e.text}
    </Text>
    {e.audio && (
      <Box mt={5}>
        <VoiceNote src={e.audio} wave={e.wave} durS={e.durS} size={size === "lg" ? "lg" : "md"} />
      </Box>
    )}
    <Tags tags={e.tags} />
  </Shell>
);

const QuoteCard = ({ e, size, link }) => {
  const mine = /^(me|pratik|pratik chawla)$/i.test(e.author || "");
  return (
    <Shell bg={mine ? PANEL : INSET}>
      <Meta e={e} link={link} />
      <Text
        fontFamily={SERIF}
        color={ACCENT}
        fontSize={size === "lg" ? "140px" : "96px"}
        lineHeight="0.6"
        mt={size === "lg" ? 10 : 8}
        mb={-2}
        aria-hidden="true"
      >
        “
      </Text>
      <Text
        fontFamily={SERIF}
        color={FG}
        fontSize={size === "lg" ? { base: "34px", md: "52px" } : { base: "28px", md: "32px" }}
        lineHeight="1.12"
        letterSpacing="-0.01em"
      >
        {e.text}
      </Text>
      {(e.author || e.source) && (
        <Flex mt={5} align="center" gap={3}>
          <Box w="20px" h="1px" bg={ACCENT} />
          <Text fontSize="sm" color={BODY}>
            <Text as="span" color={FG} fontWeight={600}>
              {e.author}
            </Text>
            {e.source && (
              <Text as="span" color={MUTED}>
                {e.author ? ", " : ""}
                {e.source}
              </Text>
            )}
          </Text>
        </Flex>
      )}
      <Tags tags={e.tags} />
    </Shell>
  );
};

const PhotoCard = ({ e, size, link }) => {
  const [open, setOpen] = useState(null);
  const imgs = e.images;
  const many = imgs.length > 1;
  const items = imgs.map((im) => ({ src: im.src, caption: im.caption || e.caption, meta: e.dateLabel }));
  return (
    <Shell pad={false}>
      {many ? (
        <Grid templateColumns="repeat(2, 1fr)" gap="2px" bg={GRID}>
          {imgs.slice(0, size === "lg" ? 6 : 4).map((im, i, arr) => (
            <Box
              key={im.src}
              as="button"
              type="button"
              aria-label={`Open photo ${i + 1}`}
              onClick={() => setOpen(i)}
              position="relative"
              gridColumn={arr.length % 2 && i === 0 ? "span 2" : undefined}
              h={arr.length % 2 && i === 0 ? { base: "200px", md: size === "lg" ? "420px" : "240px" } : { base: "140px", md: size === "lg" ? "260px" : "160px" }}
              overflow="hidden"
              bg={INSET}
              sx={{ "&:hover img": { transform: "scale(1.04)" } }}
            >
              <Image src={im.src} alt={im.caption || ""} loading="lazy" w="100%" h="100%" objectFit="cover" transition="transform .8s cubic-bezier(0.22, 1, 0.36, 1)" />
              {i === 3 && imgs.length > 4 && size !== "lg" && (
                <Flex position="absolute" inset={0} bg={alpha(BG, 0.6)} align="center" justify="center">
                  <Text color={FG} fontWeight={600} fontSize="xl">
                    +{imgs.length - 4}
                  </Text>
                </Flex>
              )}
            </Box>
          ))}
        </Grid>
      ) : (
        imgs[0] && (
          <Box
            as="button"
            type="button"
            aria-label="Open photo"
            onClick={() => setOpen(0)}
            display="block"
            w="100%"
            overflow="hidden"
            bg={INSET}
            sx={{ "&:hover img": { transform: "scale(1.03)" } }}
          >
            <Image
              src={imgs[0].src}
              alt={imgs[0].caption || e.caption || ""}
              loading="lazy"
              w="100%"
              maxH={size === "lg" ? "78vh" : "520px"}
              objectFit="cover"
              transition="transform .9s cubic-bezier(0.22, 1, 0.36, 1)"
            />
          </Box>
        )
      )}
      <Box p={{ base: 5, md: 6 }}>
        <Meta e={e} link={link} />
        {e.title && (
          <Text color={FG} fontWeight={600} fontSize="lg" mt={3} letterSpacing="-0.02em">
            {e.title}
          </Text>
        )}
        {e.caption && (
          <Text color={BODY} fontSize={size === "lg" ? "lg" : "md"} mt={e.title ? 1 : 3} lineHeight="1.55">
            {e.caption}
          </Text>
        )}
        {e.location && (
          <Text fontFamily={MONO} fontSize="10px" letterSpacing="0.12em" textTransform="uppercase" color={MUTED} mt={3}>
            ⌖ {e.location}
          </Text>
        )}
        <Tags tags={e.tags} />
      </Box>
      <Lightbox items={items} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
    </Shell>
  );
};

const VideoCard = ({ e, size, link }) => (
  <Shell pad={false}>
    <VideoEmbed video={e.video} poster={e.poster} title={e.title || e.caption || "Video"} radius="0" ratio={e.ratio || 16 / 9} />
    <Box p={{ base: 5, md: 6 }}>
      <Meta e={e} link={link} />
      {e.title && (
        <Text color={FG} fontWeight={600} fontSize={size === "lg" ? "2xl" : "lg"} mt={3} letterSpacing="-0.02em">
          {e.title}
        </Text>
      )}
      {e.caption && (
        <Text color={BODY} fontSize={size === "lg" ? "lg" : "md"} mt={e.title ? 1 : 3} lineHeight="1.55">
          {e.caption}
        </Text>
      )}
      <Tags tags={e.tags} />
    </Box>
  </Shell>
);

const LinkCard = ({ e, size, link }) => {
  const spotify = spotifyEmbed(e.url);
  return (
    <Shell pad={false}>
      {spotify ? (
        <Box
          as="iframe"
          src={spotify.src}
          title={e.title || "Spotify"}
          w="100%"
          h={spotify.tall ? "352px" : "152px"}
          border="0"
          display="block"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
      ) : (
        e.image && (
          <Link href={e.url} isExternal display="block" bg={INSET} overflow="hidden" sx={{ "&:hover img": { transform: "scale(1.03)" } }}>
            <Image src={e.image} alt="" loading="lazy" w="100%" maxH={size === "lg" ? "60vh" : "300px"} objectFit="cover"
              transition="transform .9s cubic-bezier(0.22, 1, 0.36, 1)" />
          </Link>
        )
      )}
      <Box p={{ base: 5, md: 6 }}>
        <Meta e={e} link={link} />
        <Link href={e.url} isExternal display="block" mt={4} role="group" _hover={{ textDecoration: "none" }}>
          <Text fontFamily={MONO} fontSize="11px" color={ACCENT} letterSpacing="0.04em">
            {hostOf(e.url)} ↗
          </Text>
          <Text color={FG} fontWeight={600} fontSize={size === "lg" ? "2xl" : "lg"} letterSpacing="-0.02em" mt={1} lineHeight="1.3"
            _groupHover={{ color: ACCENT }} transition="color .2s" wordBreak="break-word">
            {e.title || e.url}
          </Text>
        </Link>
        {e.caption && (
          <Text color={BODY} fontSize={size === "lg" ? "lg" : "md"} mt={2} lineHeight="1.55" whiteSpace="pre-line">
            {e.caption}
          </Text>
        )}
        <Tags tags={e.tags} />
      </Box>
    </Shell>
  );
};

const CARDS = { thought: ThoughtCard, quote: QuoteCard, photo: PhotoCard, video: VideoCard, link: LinkCard };

const JournalCard = ({ entry, size = "md", link = true }) => {
  const C = CARDS[entry.type] || ThoughtCard;
  return <C e={entry} size={size} link={link} />;
};

export default JournalCard;
